/**
 * TablaBloque — una tabla del informe del día con su subtotal.
 *
 * NO SE RENDERIZA SI NO TIENE FILAS. El bloque «sin clasificar» sólo aparece cuando hay
 * cargos que no se pudieron clasificar, y su presencia es la señal de que hay datos que
 * arreglar. Un encabezado vacío en el papel diría que la compañía no vendió ni cobró, que
 * es otra cosa.
 *
 * @see src/components/payments/yoolpay/informe/documento.js
 */
import React from 'react';

import { TINTA, fmtClpInforme, fmtUf } from './documento';

const th = (extra = {}) => ({
  textAlign: 'left',
  fontSize: 8,
  fontWeight: 700,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: TINTA.rotulo,
  borderBottom: `1.5px solid ${TINTA.texto}`,
  padding: '0 8px 5px 0',
  ...extra,
});

const td = (extra = {}) => ({
  fontSize: 10,
  padding: '6px 8px 6px 0',
  borderBottom: `1px solid ${TINTA.lineaFina}`,
  ...extra,
});

const NUMERO = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const MONO = { fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 9.5 };
const PIE = { padding: '6px 8px 0 0', borderTop: `1px solid ${TINTA.texto}` };

export default function TablaBloque({ titulo, bajada, bloque }) {
  if (!bloque || !Array.isArray(bloque.filas) || bloque.filas.length === 0) return null;

  const n = bloque.filas.length;

  return (
    <>
      <h2
        style={{
          margin: '22px 0 0',
          fontSize: 12.5,
          fontWeight: 700,
          letterSpacing: '.02em',
          breakAfter: 'avoid',
        }}
      >
        {titulo}
        {bajada ? <span style={{ fontWeight: 400, color: TINTA.tenue }}> · {bajada}</span> : null}
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', margin: '8px 0 0' }}>
        <thead>
          <tr>
            <th style={th({ width: '21%' })}>Orden de pago</th>
            <th style={th({ width: '30%' })}>Cliente</th>
            <th style={th()}>Descripción</th>
            <th style={th({ ...NUMERO, width: '12%' })}>UF</th>
            <th style={th({ ...NUMERO, width: '15%', padding: '0 0 5px 0' })}>CLP</th>
          </tr>
        </thead>
        <tbody>
          {bloque.filas.map((f, i) => (
            // La clave lleva el índice porque una compañía puede tener dos filas con el
            // mismo código: un carrito con dos productos de la misma aseguradora.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`${f.orden_pago ?? 'sin'}-${i}`}>
              <td style={td(MONO)}>
                {f.orden_pago ?? '—'}
                {/* La marca de compartido va en la fila porque es donde explica algo: por
                    qué el monto no es el del cargo completo. */}
                {f.compartido ? (
                  <span
                    style={{ color: TINTA.tenue, fontSize: 8 }}
                    title="Cobro repartido con otra compañía"
                  >
                    {' '}
                    ·∥
                  </span>
                ) : null}
              </td>
              <td style={td()}>{f.cliente ?? '—'}</td>
              <td style={td({ color: TINTA.suave })}>{f.descripcion ?? '—'}</td>
              <td style={td(NUMERO)}>{fmtUf(f.uf)}</td>
              <td style={td({ ...NUMERO, padding: '6px 0' })}>{fmtClpInforme(f.clp)}</td>
            </tr>
          ))}
          <tr>
            <td
              colSpan={3}
              style={{
                ...PIE,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                color: TINTA.suave,
              }}
            >
              Subtotal {titulo.toLowerCase()} · {n} {n === 1 ? 'orden' : 'órdenes'}
            </td>
            <td style={{ ...PIE, ...NUMERO, fontSize: 10.5, fontWeight: 700 }}>
              {fmtUf(bloque.uf)}
            </td>
            <td
              style={{ ...PIE, ...NUMERO, fontSize: 10.5, fontWeight: 700, padding: '6px 0 0' }}
            >
              {fmtClpInforme(bloque.clp)}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
